import { maybe, rd, type RemoteData } from '@passionware/monads';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import type { MergeServices } from 'v5/platform/ts/services';
import type { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import type { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import type { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import {
  resourceFieldCatalog,
  type ResourceFieldGroup
} from './resourceFieldCatalog.logic';

export type ResourceFieldCatalogServices = MergeServices<
  [
    WithAppScopeService,
    WithResourceTypeService,
    WithMetastructureMappingService
  ]
>;

/** The resource fields of the browsing scope's resource types. */
export function useResourceFieldCatalog(
  services: ResourceFieldCatalogServices
): RemoteData<ResourceFieldGroup[]> {
  const scope = rd.tryMap(
    services.appScopeService.useResolvedAppScope(),
    appScopeUtils.identityFromResolved
  );
  const types = services.resourceTypeService.useResourceTypes(scope);
  const requests =
    services.metastructureMappingService.useMetastructureAggregateRequests(
      maybe.map(scope, identity => ({
        type: 'resource' as const,
        scope: identity
      }))
    );
  const metastructures =
    services.metastructureMappingService.useMetastructures(requests);
  return rd.useMemoMapMonadic(
    rd.useCombine({ types, metastructures: rd.combineAll(metastructures) }),
    ({ types, metastructures }) =>
      rd.of(
        resourceFieldCatalog({
          types: requests.map((request, index) => ({
            name:
              types.find(type => type.id === request.subtypeId)?.name ??
              String(request.subtypeId),
            fields: metastructures[index] ?? []
          }))
        })
      )
  );
}
